import { React, useEffect, useState } from 'react';
import { selectFerpaState } from '../../../../../../features/authentication/authSlice';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import ReportOptions from '../../components/reportOptions';
import TableSkeleton from '../../../../../../components/SkeletonLoader/TableSkeleton';
import { DateFormatter } from '../../../../../../utils/DateFormatter';
import { colorCode } from '../../../../utils/constants';
import { useGetAllStudentAssignmentsScoreMutation } from '../../../../../../features/authentication/reportApiSlice';
import CustomToast from '../../../../../../components/Toast';
import {ScoreCalculator} from '../../../../utils/ScoreCalculator';
import { useReportContext } from '../../../../components/reportContext';
import ReportHeader from '../../components/reportHeader';
import FERPA from '../../../../../../components/FERPA/FerpaComponent';
import { convertToCSV, downloadCSV, reorderObjectProperties } from '../../../../../assignments/utils';
import { usePdfGeneratorMutation } from '../../../../../../features/authentication/pdfGeneratorApiSlice';
import {
  useGetTeacherReportOptionsMutation
} from '../../../../../../features/authentication/persistApiSlice';

const AllStudentsScoreInfo = () => {
  const navigate = useNavigate();
  const ferpaEnabled = useSelector(selectFerpaState);
  const { selectedCourseId, startDate, endDate, courseName, studentToIncludeTab, className, selectedClassId, isAssessment } = useReportContext();
  const [pdfGenerator] = usePdfGeneratorMutation();

  const [reportDate, setReportDate] = useState('');
  const [expand, setExpand] = useState(false);
  const [isLoadingReportData, setIsLoadingReportData] = useState(true);
  const [assignmentDetails, setAssignmentDetails] = useState([]);
  const [studentDetails, setStudentDetails] = useState([]);
  const [unansweredAsIncorrect, setUnansweredAsIncorrect] = useState(null);
  const [retryScore, setRetryScore] = useState(null);
  const [displayName, setDisplayName] = useState(null);
  const [displayId, setDisplayId] = useState(null);
  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [isCsvGenerating, setIsCsvGenerating] = useState(false);
  const [assignmentScoreDetails, setAssignmentScoreDetails] = useState([]);
  const [dateTimeBasedUniqueKey, setDateTimeBasedUniqueKey] = useState(
    new Date().getTime().toString()
  );

  const [getAllStudentAssignmentsScoreMutation] = useGetAllStudentAssignmentsScoreMutation();
  const [getTeacherReportOptions] = useGetTeacherReportOptionsMutation();

  useEffect(() => {
    checkContextData();
    getReportOptions();
  }, []);

  const checkContextData = () => {
    if(selectedCourseId == -1)
      navigate(-1);
  };

  const getReportOptions = async () => {
    let response = await getTeacherReportOptions().unwrap();
    setUnansweredAsIncorrect(response.incompleteScoring);
    setRetryScore(response.retryScore);
    setDisplayName(response.displayName);
    setDisplayId(response.displayId);

    await handleReportData(response.retryScore, response.incompleteScoring);
    setIsLoadingReportData(false);
  };

  const handleReportData = async (retryScore, incompleteScoring) => {
    if(selectedCourseId == -1)
      return;

    try{
      const options={
        classId: selectedClassId,
        startDate: DateFormatter.toShortDate2(startDate),
        endDate: DateFormatter.toShortDate2(endDate),
        courseId: selectedCourseId,
        isAssessment: isAssessment
      };

      let response = await getAllStudentAssignmentsScoreMutation(options);
      if (response.error) {
        CustomToast(response.error.data.error, 'error');
      }
      else {
        setReportDate(response.data.reportDate);
        setAssignmentDetails(response.data.assignmentDetails);
        setStudentDetails(response.data.studentDetails);
        updateAssignmentDetails(response.data.assignmentDetails, response.data.studentDetails, retryScore, incompleteScoring);
      }
    } catch (error) {
      console.log(error);
    }
    setDateTimeBasedUniqueKey(new Date().getTime().toString());
  };

  const updateAssignmentDetails = (assignments, studentScores, retryScore, incompleteScoring) => {
    assignments = assignments ?? assignmentDetails;
    studentScores = studentScores ?? studentDetails;
    let studentAssignmentDetails=[];
    
    studentScores.map(student => {
      let scoreDetails = [];

      assignments.forEach(assignment => {
        const { assignmentId } = assignment;
        const scoreDetail = student.scoreDetails.find(detail => detail.assignmentId === assignmentId);
        
        if (scoreDetail) {
          let score = ScoreCalculator(retryScore, incompleteScoring ? 1 : 0, scoreDetail.assignmentScoreCount, scoreDetail.correct, scoreDetail.incorrect, scoreDetail.correctOnRetry, scoreDetail.crPoints, scoreDetail.crAnswered, scoreDetail.crNotAnswered, scoreDetail.crMaxPoints, scoreDetail.statusNewCount);
          scoreDetails.push(score>=0 && score<=100 ? `${score}%` : score);
        } else {
          scoreDetails.push('');
        }
      });

      let assignmentDetail = {
        studentName: student.name,
        studentId: student.id,
        scores: scoreDetails 
      };
      studentAssignmentDetails.push(assignmentDetail);
    });
    setAssignmentScoreDetails(studentAssignmentDetails);
  };

  const exportHandler = () => {
    const timePeriod = `${DateFormatter.toShortDate2(startDate)} to ${DateFormatter.toShortDate2(endDate)}`;
    const reportDetails = {
      Report : `${isAssessment ? 'Assessment' : 'Assignment'} Scores for ${studentToIncludeTab == 0 ? 'All Students' : studentToIncludeTab == 1 && selectedClassId == -1 ? 'All Classes' : className}`,
      Course : courseName,
      'Time Period': timePeriod,
      'Report Date': DateFormatter.toLongDateTime(reportDate, true)
    };

    const studentReportData = [];
    const assignmentReportData = [];
    const scoreDescription = [];

    assignmentScoreDetails.forEach((student) => {
      const studentData = {
        'Student Name': ferpaEnabled ? student.studentName : 'name hidden',
        'Student ID': ferpaEnabled ? student.studentId : 'id hidden'
      };

      for (let i = 0; i < assignmentDetails.length; i++) {
        const assignmentKey = 'A' + (i + 1).toString();
        studentData[assignmentKey] = student.scores?.[i] || '';
      }

      studentReportData.push(studentData);
    });

    assignmentDetails.forEach((report, index) => {
      assignmentReportData.push({
        'No': `A${index + 1}`,
        'Assignment Name': report.description,
        'Last Assigned': DateFormatter.toShortDate2(report.assignDate)
      });      
    });

    scoreDescription.push({
      'Not Scored': 'NS',
      'Not Started': 'N',
      'In Progress': 'P'
    });
    
    const newData = {
      ReportOptions : reportDetails,
      ',' : studentReportData,
      'Assignments' : assignmentReportData,
      '' : scoreDescription
    };

    const newDataWithReportOptionsFirst = reorderObjectProperties(newData, 'ReportOptions');
    const csvData = convertToCSV(newDataWithReportOptionsFirst);
    downloadCSV(csvData, 'All Assignment All Students Score' );
    setIsCsvGenerating(false);
  };

  const printHandler = async () => {
    const contentWrapper = document.getElementById('reportWrapper');
    const htmlString = contentWrapper.innerHTML;

    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlString;

    const ferpaContainer = tempContainer.querySelector('#ferpaContainer');
    if (ferpaContainer) {
      ferpaContainer.classList.add('d-none');
    }

    const buttonElements = tempContainer.querySelectorAll('button');
    buttonElements.forEach((svgElement) => {
      svgElement.classList.add('d-none');
    });

    const reportOptionBtn = tempContainer.querySelector('#reportOptionsDiv');
    if(reportOptionBtn) {
      reportOptionBtn.classList.add('d-none');
    }

    const reportOptions = tempContainer.querySelector('#reportOptions');
    if(reportOptions) {
      reportOptions.classList.remove('d-none');
    }

    const base64Encoded = btoa(tempContainer.innerHTML);
    const options = JSON.stringify({
      htmlContent: base64Encoded,
      OutputFileName: 'allAssignmentSingleStudentReport'
    });

    try {
      var pdfBlob = await pdfGenerator(options).unwrap();
      console.log(pdfBlob);
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = 'All_Assignment_Single_Student_Score.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(pdfUrl);
      setIsPdfGenerating(false);
    } catch (error) {
      setIsPdfGenerating(false);
      console.error(error);
    }
  };

  return (
    <div className="white-card">
      <div id="reportWrapper">
        <div className="d-flex align-items-center justify-content-between pb-4">
          <h2 className="primary-title mb-md-0">{`${isAssessment ? 'Assessment' : 'Assignment'} Scores for ${ studentToIncludeTab == 0 ? 'All Students' : studentToIncludeTab == 1 && selectedClassId == -1 ? 'All Classes' : className}`}</h2>
          <div id="ferpaContainer" className="d-flex justify-content-between buttonGroup align-items-center">
            <FERPA />
            <button id="back_button" onClick={() => navigate(-1)} className="btn btn-width btn-castleWhite">
              Back
            </button>
            <button id="export_button" onClick={async() => {setIsCsvGenerating(true); exportHandler();}} className="btn btn-width btn-castleOrange p-0" disabled={isCsvGenerating || isLoadingReportData}>
              {isCsvGenerating == true ? 'Generating CSV' : 'Export'}
            </button>
            <button id="print_button" onClick={async() => {setIsPdfGenerating(true); printHandler();}} className="btn btn-width btn-castleRed p-0" disabled={isPdfGenerating || isLoadingReportData}>
              {isPdfGenerating ? 'Generating PDF' : 'Print to PDF'}
            </button>
          </div>
        </div>
        <ReportHeader
          isLoading={isLoadingReportData}
          startDate={startDate}
          endDate={endDate}
          reportDate={reportDate}
          courseName={courseName}
        />
        <div className='align-items-baseline d-flex justify-content-between pt-3'>
          <div id="reportOptionsDiv" className='d-flex fw-medium text-castleOrange cursor-pointer report-button'
            onClick={() => setExpand(!expand)}>
            Report Options
            <div className='align-items-center cursor-pointer d-flex'>
              <FontAwesomeIcon className='fs-7 ms-2' icon={expand ? faMinus : faPlus} />
            </div>
          </div>
          <div id="scoringDescription" className='d-flex pb-4'>
            {colorCode.map((item) => (
              <div key={item.name} className='align-items-center d-flex px-4'>
                <div className='d-flex rounded-circle justify-content-center' style={{ backgroundColor: item.backgroundColor }}>
                  {item.icon}
                </div>
                <span className='px-2'>{item.name}</span>
              </div>
            ))}
          </div>
        </div>
        <ReportOptions
          key={dateTimeBasedUniqueKey}
          expand={expand}
          isSingleStudent={false}
          retryScore={retryScore}
          setRetryScore={setRetryScore}
          unansweredAsIncorrect={unansweredAsIncorrect}
          setUnansweredAsIncorrect={setUnansweredAsIncorrect}
          displayName={displayName}
          setDisplayName={setDisplayName}
          displayId={displayId}
          setDisplayId={setDisplayId}
          updateAssignmentDetails={updateAssignmentDetails}
        />
        <div className='pt-4'>
          <div id='studentsList' className='pb-4'>
            <div className='assignments-scroll-bar custom-table table-responsive p-0'>
              <table className='table reportTable'>
                {
                  studentDetails?.length > 0 && !isLoadingReportData ? (
                    <>
                      <thead className='thead-light align-middle'>
                        <tr>
                          {displayName &&
                            <th id='studentName' scope="col" className='fw-medium'>
                              Student Name
                            </th>
                          }
                          {displayId &&
                            <th id='studentId' scope="col" className='fw-medium'>
                              Student ID
                            </th>
                          }
                          {[...Array(assignmentDetails.length)].map((_, index) => (
                            <th id={'assignmentNo' + index} key={'assignment'+ (index+1)} scope="col" className='fw-medium text-center p-0'>
                              A{index+1}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {assignmentScoreDetails?.map((item) => (
                          <tr key={item.Id}>
                            {displayName &&
                              <td>{ferpaEnabled ? item.studentName : 'name hidden'}</td>
                            }
                            {displayId &&
                              <td>{ferpaEnabled ? item.studentId : 'id hidden'}</td>
                            }
                            {item.scores.map((score, index) => {
                              let colorItem = colorCode.find(item => item.label == score);
                              return (
                                <td key={'score' + index} className='text-center'>
                                  {colorItem ?
                                    <span className='d-flex justify-content-center'>
                                      <div className='rounded-circle circleBtn' style={{ backgroundColor: colorItem.backgroundColor }}>
                                        {colorItem.icon}
                                      </div>
                                    </span>
                                    : score}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </>
                  ) : isLoadingReportData && (
                    <>
                      <TableSkeleton
                        colCount={8}
                        rowCount={6}
                      />
                    </>
                  )
                }
              </table>
            </div>
          </div>

          <div id='assignmentList' className='pt-3'>
            <h2 className='fw-medium pb-3'>Assignments</h2>
            <div className='assignments-scroll-bar custom-table table-responsive p-0'>
              <table className='table reportTable'>
                {
                  assignmentDetails.length > 0 && !isLoadingReportData ? (
                    <>
                      <thead className='thead-light align-middle'>
                        <tr>
                          <th id='no' scope='col' className='fw-medium'>
                            No.
                          </th>
                          <th id='description' scope="col" className='fw-medium min-w-400'>
                            Assignment Name
                          </th>
                          <th id='assignDate' scope="col" className='fw-medium'>
                            Last Assigned
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {assignmentDetails?.map((item, index) => (
                          <tr key={item.assignmentId}>
                            <td>A{index+1}</td>
                            <td className='w-min-max-400 text-truncate'>{item.description}</td>
                            <td>{DateFormatter.toShortDate2(item.assignDate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </>
                  ) : isLoadingReportData ? (
                    <>
                      <TableSkeleton
                        colCount={3}
                        rowCount={6}
                      />
                    </>
                  ) :
                    <p className='text-danger'>There are no results for the selected report options.</p>
                }
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllStudentsScoreInfo;
